/**
 * RFC 4180 CSV, shared by the API (imports) and the SPA (templates). Fields may be quoted with `"`; a
 * quote inside a quoted field is doubled. Records end with CRLF, LF or CR. A leading UTF-8 BOM (what
 * Excel writes) is dropped. Blank lines are skipped. Text is kept as is (Arabic included); only the
 * caller trims.
 */

export class CsvError extends Error {
  constructor(
    message: string,
    /** 1-based line of the record where the problem starts. */
    readonly line: number,
  ) {
    super(message)
    this.name = 'CsvError'
  }
}

/** One parsed record with the 1-based line it starts on (quoted fields may span lines). */
export interface CsvRecord {
  line: number
  fields: string[]
}

export function parseCsvRecords(text: string): CsvRecord[] {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text
  const out: CsvRecord[] = []
  let fields: string[] = []
  let field = ''
  let line = 1
  let start = 1
  let quoted = false
  let i = 0
  const endRecord = () => {
    fields.push(field)
    // A blank line is one empty field: skip it.
    if (!(fields.length === 1 && fields[0] === '')) out.push({ line: start, fields })
    fields = []
    field = ''
  }
  while (i < src.length) {
    const c = src[i] as string
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"'
          i += 2
          continue
        }
        quoted = false
        i++
        const next = src[i]
        if (next !== undefined && next !== ',' && next !== '\r' && next !== '\n') {
          throw new CsvError('Unexpected text after a closing quote', start)
        }
        continue
      }
      if (c === '\n' || (c === '\r' && src[i + 1] !== '\n')) line++
      field += c
      i++
      continue
    }
    if (c === '"') {
      if (field !== '') throw new CsvError('A quote may only open a field', line)
      quoted = true
      i++
    } else if (c === ',') {
      fields.push(field)
      field = ''
      i++
    } else if (c === '\r' || c === '\n') {
      endRecord()
      i += c === '\r' && src[i + 1] === '\n' ? 2 : 1
      line++
      start = line
    } else {
      field += c
      i++
    }
  }
  if (quoted) throw new CsvError('A quoted field is not closed', start)
  if (field !== '' || fields.length > 0) endRecord()
  return out
}

/** The records' fields only. */
export function parseCsv(text: string): string[][] {
  return parseCsvRecords(text).map((r) => r.fields)
}

/**
 * A text cell neutralised against formula injection (OWASP "CSV injection"), for CSV written from data
 * the user didn't type as a file (an export, a document read by the assistant): a leading `=`, `+`, `-` or
 * `@` gets an apostrophe, so a spreadsheet shows it as text. The asset import reads such a cell back
 * without the apostrophe ("'-5" is "-5").
 */
export function csvSafe(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null
  return /^[=+\-@]/.test(value) ? `'${value}` : value
}

/** Quotes a field when it holds a comma, a quote or a line break. */
export function csvField(value: string | null | undefined): string {
  const v = value ?? ''
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
}

/** Rows to CSV text with CRLF line ends (no BOM; add one for Excel when downloading). */
export function toCsv(rows: (string | null | undefined)[][]): string {
  return `${rows.map((r) => r.map(csvField).join(',')).join('\r\n')}\r\n`
}
